import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * TikTok30DayChallengePage — a print-optimized 30-day content + monetization
 * plan for Mike Manene's @freightpilotai TikTok account (FreightPilotAI brand).
 *
 * Route: /tiktok-30day
 *
 * How to use:
 *   1. Open /tiktok-30day
 *   2. Customize name/handle/start date on-page if needed
 *   3. Ctrl+P → Save as PDF (or print to paper and pin to wall)
 *   4. Check off each day as you complete it
 *
 * Query params (optional — sensible defaults):
 *   name    — creator's name
 *   handle  — TikTok handle
 *   start   — start date (any parseable string, e.g. "2026-08-10")
 */
export default function TikTok30DayChallengePage() {
  const [params] = useSearchParams();
  const [name, setName] = useState((params.get("name") || "Mike Manene").trim());
  const [handle, setHandle] = useState((params.get("handle") || "@freightpilotai").trim());
  const [startDate, setStartDate] = useState(params.get("start") || "");

  useEffect(() => {
    document.title = "TikTok 30-Day Reset & Monetization Challenge";
  }, []);

  // Compute date for each day if a start date was provided
  const startDateObj = startDate ? new Date(startDate + "T00:00:00") : null;
  const dateForDay = (dayNum) => {
    if (!startDateObj || isNaN(startDateObj)) return null;
    const d = new Date(startDateObj);
    d.setDate(d.getDate() + (dayNum - 1));
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <div className="min-h-screen bg-neutral-100 text-black">
      <style>{`
        @page { size: Letter portrait; margin: 0; }
        @media print {
          .no-print { display: none !important; }
          html, body { background: #fff !important; }
          .challenge-sheet { box-shadow: none !important; margin: 0 !important; page-break-after: always; }
          .challenge-sheet:last-child { page-break-after: auto; }
          .day-row input[type="checkbox"] { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        .day-row input[type="checkbox"] {
          width: 14px; height: 14px; accent-color: #FF5A1F;
        }
      `}</style>

      {/* Screen-only sticky bar */}
      <div className="no-print bg-neutral-900 text-white text-sm py-3 px-6 sticky top-0 z-10 flex items-center justify-between gap-4">
        <div>
          <b>Ready to print.</b>{" "}
          Press <kbd className="px-1.5 py-0.5 bg-neutral-700 rounded text-xs">Ctrl+P</kbd> (Windows) or{" "}
          <kbd className="px-1.5 py-0.5 bg-neutral-700 rounded text-xs">Cmd+P</kbd> (Mac). Choose <b>"Save as PDF"</b>.
        </div>
        <button
          onClick={() => window.print()}
          className="bg-[#FF5A1F] hover:bg-[#FF7038] text-black font-semibold rounded-md px-4 py-2 text-sm shrink-0"
          data-testid="challenge-print-btn"
        >
          Print now
        </button>
      </div>

      {/* Screen-only customize block */}
      <div className="no-print max-w-3xl mx-auto mt-6 mb-4 p-5 rounded-lg border-2 border-[#FF5A1F] bg-white shadow-lg">
        <div className="font-bold text-neutral-900 mb-1 text-base">
          📝 Customize before printing (optional)
        </div>
        <div className="text-xs text-neutral-600 mb-4">
          Set a start date and each day will get its own real calendar date on the printout.
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-neutral-500 font-bold block mb-1">
              Your name
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-neutral-300 rounded px-3 py-2 text-sm focus:border-[#FF5A1F] focus:outline-none"
              data-testid="challenge-input-name"
            />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-neutral-500 font-bold block mb-1">
              TikTok handle
            </span>
            <input
              type="text"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              className="w-full border border-neutral-300 rounded px-3 py-2 text-sm focus:border-[#FF5A1F] focus:outline-none"
              data-testid="challenge-input-handle"
            />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-neutral-500 font-bold block mb-1">
              Start date (optional)
            </span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full border border-neutral-300 rounded px-3 py-2 text-sm focus:border-[#FF5A1F] focus:outline-none"
              data-testid="challenge-input-startdate"
            />
          </label>
        </div>
      </div>

      {/* ═══════════════ PAGE 1: COVER + WEEKS 1–2 ═══════════════ */}
      <div
        className="challenge-sheet mx-auto my-6 bg-white shadow-lg"
        style={{ width: "8.5in", minHeight: "11in", padding: "0.55in 0.7in" }}
        data-testid="challenge-page-1"
      >
        {/* Brand strip */}
        <div className="flex items-center justify-between border-b-2 border-[#FF5A1F] pb-3 mb-4">
          <div className="flex items-baseline gap-1">
            <span className="font-black text-2xl tracking-tight">Freight</span>
            <span className="font-black text-2xl tracking-tight text-[#FF5A1F]">Pilot</span>
            <span className="font-black text-2xl tracking-tight">AI</span>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-neutral-600 font-semibold">
              Prepared for
            </div>
            <div className="text-sm font-bold">{name} · {handle}</div>
          </div>
        </div>

        {/* Title */}
        <div className="mb-5">
          <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-1">
            30-Day Challenge · TikTok Reset + Monetization
          </div>
          <h1 className="text-[38px] leading-[1.02] font-black tracking-tight mb-3">
            From <span className="text-[#FF5A1F]">81K stalled</span> to a monetized founder-journey account in 30 days.
          </h1>
          <p className="text-[13px] text-neutral-700 leading-snug">
            A daily action plan for turning an AI-slop account into an authentic "software founder → trucking startup" channel that (a) stops the follower bleed, (b) activates TikTok Shop revenue, and (c) drives real signups to FreightPilotAI. Face-free video formats. No AI-generated humans. Real story only.
          </p>
        </div>

        {/* Current state / target state */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="rounded-lg border border-neutral-300 bg-neutral-50 p-3">
            <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold mb-1">
              Where you are today
            </div>
            <ul className="text-[12px] text-neutral-800 leading-snug space-y-0.5">
              <li>· 81.3K followers (down from 102K)</li>
              <li>· Following 9,821 (algo-suppressing)</li>
              <li>· 103.7K likes (weak 1.27× ratio)</li>
              <li>· TikTok Shop &amp; Affiliate active</li>
              <li>· Brand DMs waiting unanswered</li>
              <li>· Content: AI-slop trucker images</li>
            </ul>
          </div>
          <div className="rounded-lg border-2 border-[#FF5A1F] bg-[#FFF8F3] p-3">
            <div className="text-[9px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-1">
              Where you'll be in 30 days
            </div>
            <ul className="text-[12px] text-neutral-800 leading-snug space-y-0.5">
              <li>· Follower count stable or growing</li>
              <li>· Following under 500</li>
              <li>· 13+ authentic videos posted</li>
              <li>· 1–2 brand deals closed at real rates</li>
              <li>· TikTok Shop revenue flowing</li>
              <li>· 50+ new FreightPilotAI signups</li>
            </ul>
          </div>
        </div>

        {/* WEEK 1 */}
        <WeekBlock number="1" title="Reset — clean house, kill the algorithm suppression" dateForDay={dateForDay}>
          <Day n={1} label="Unfollow 2,000 accounts (target: down to ~7,800)" />
          <Day n={2} label="Unfollow 2,000 more (target: down to ~5,800)" />
          <Day n={3} label="Unfollow 2,000 more (target: down to ~3,800)" />
          <Day n={4} label="Unfollow 2,000 more (target: down to ~1,800)" />
          <Day n={5} label="Unfollow final ~1,400 accounts (target: under 500 following)" />
          <Day n={6} label='Rewrite bio → "Software founder → building my first trucking company." Delete off-niche playlists (World Cup). Delete AI-slop trucker videos.' />
          <Day n={7} label='POST VIDEO #1: "Why I&#39;m going from software to trucking" · voice + text slideshow · 60 sec' />
        </WeekBlock>

        {/* WEEK 2 */}
        <WeekBlock number="2" title="Build — the founder-journey series begins" dateForDay={dateForDay}>
          <Day n={8} label='POST VIDEO #2: "Real cost breakdown: 2026 Freightliner Cascadia" · dealer screenshots + voice' />
          <Day n={9} label="Review all pending brand DMs · reply with rate card template (see back page)" />
          <Day n={10} label='POST VIDEO #3: "3 trucking scams I already got hit with (and I don&#39;t own a truck yet)"' />
          <Day n={11} label="Pick 5 TikTok Shop products aligned with your story · add to your product showcase" />
          <Day n={12} label='POST VIDEO #4: "What I&#39;m building — FreightPilotAI in 60 seconds" · screen recording + voice' />
          <Day n={13} label='Engage: comment on 20 other trucking creators&#39; posts · reply to every DM · 1 hour block' />
          <Day n={14} label='POST VIDEO #5: "How I&#39;m choosing my first driver (as a total outsider)"' />
        </WeekBlock>

        {/* Footer of page 1 */}
        <div className="mt-4 pt-3 border-t border-neutral-300 flex items-center justify-between text-[10px] text-neutral-500">
          <div>
            <b className="text-neutral-900">FreightPilotAI</b> · TikTok 30-Day Reset Challenge · Personal plan for {name}
          </div>
          <div>Page 1 of 2 · continued →</div>
        </div>
      </div>

      {/* ═══════════════ PAGE 2: WEEKS 3–4 + RATE CARD ═══════════════ */}
      <div
        className="challenge-sheet mx-auto my-6 bg-white shadow-lg"
        style={{ width: "8.5in", minHeight: "11in", padding: "0.55in 0.7in" }}
        data-testid="challenge-page-2"
      >
        <div className="flex items-center justify-between border-b-2 border-[#FF5A1F] pb-3 mb-4">
          <div className="flex items-baseline gap-1">
            <span className="font-black text-lg tracking-tight">Freight</span>
            <span className="font-black text-lg tracking-tight text-[#FF5A1F]">Pilot</span>
            <span className="font-black text-lg tracking-tight">AI</span>
            <span className="ml-3 text-[11px] text-neutral-500 uppercase tracking-widest">
              30-Day Challenge · Weeks 3–4
            </span>
          </div>
          <div className="text-[11px] text-neutral-500">Page 2 of 2</div>
        </div>

        {/* WEEK 3 */}
        <WeekBlock number="3" title="Monetize — turn on the TikTok Shop revenue faucet" dateForDay={dateForDay}>
          <Day n={15} label="Order 1 TikTok Shop product to test on camera (dashcam / phone mount / business tool)" />
          <Day n={16} label='POST VIDEO #6: Hands-only unboxing · "This just arrived for the trucking company I&#39;m about to start"' />
          <Day n={17} label="Reply to all DMs · negotiate first brand deal (see rate card) · get 50% deposit before shooting" />
          <Day n={18} label='POST VIDEO #7: Product comparison (Format 2) · 2 products side-by-side · ask audience to help decide' />
          <Day n={19} label="Add affiliate links to top 3 pinned videos · pin the highest-performing one" />
          <Day n={20} label='POST VIDEO #8: "5 things nobody tells first-time carriers about starting a trucking company"' />
          <Day n={21} label="Weekly review · track: follower change, view spikes, TikTok Shop revenue, brand deal pipeline" />
        </WeekBlock>

        {/* WEEK 4 */}
        <WeekBlock number="4" title="Scale — brand deals, consistency, month-2 planning" dateForDay={dateForDay}>
          <Day n={22} label='POST VIDEO #9: "Interviewing my first driver — the exact questions I&#39;m asking"' />
          <Day n={23} label="Proactive outreach: send your rate card to 5 brands you actually would want to promote" />
          <Day n={24} label='POST VIDEO #10: "Real cost of owning 1 truck (per month, honest numbers)"' />
          <Day n={25} label="Accept first brand deal at proper rate · sign contract · collect deposit" />
          <Day n={26} label="POST VIDEO #11: Brand deal video (follow the contract deliverables exactly, use #ad hashtag)" />
          <Day n={27} label='POST VIDEO #12: "Q&amp;A — ask me anything about starting a trucking business as an outsider"' />
          <Day n={28} label="Engage with all comments · DM the 10 most promising ones (potential FreightPilotAI customers)" />
          <Day n={29} label='POST VIDEO #13: "Day 1 of owning my first truck (soon)" · countdown-style, high emotion' />
          <Day n={30} label="MONTHLY REVIEW · analyze wins/losses · plan Month 2 · celebrate 🎉" />
        </WeekBlock>

        {/* Rate card */}
        <div className="rounded-xl border-2 border-neutral-900 p-4 mb-4">
          <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-2">
            Your rate card · @{handle.replace(/^@/, "")} · 81K followers
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
            <div className="flex justify-between border-b border-neutral-200 pb-1">
              <span className="text-neutral-700">Dedicated video (1 post)</span>
              <span className="font-bold">$500–$1,500</span>
            </div>
            <div className="flex justify-between border-b border-neutral-200 pb-1">
              <span className="text-neutral-700">Story mention</span>
              <span className="font-bold">$100–$300</span>
            </div>
            <div className="flex justify-between border-b border-neutral-200 pb-1">
              <span className="text-neutral-700">Bio link (30 days)</span>
              <span className="font-bold">$200–$500</span>
            </div>
            <div className="flex justify-between border-b border-neutral-200 pb-1">
              <span className="text-neutral-700">3-post package</span>
              <span className="font-bold">$1,500–$3,500</span>
            </div>
            <div className="flex justify-between border-b border-neutral-200 pb-1">
              <span className="text-neutral-700">Exclusivity (30 days)</span>
              <span className="font-bold">+50% premium</span>
            </div>
            <div className="flex justify-between border-b border-neutral-200 pb-1">
              <span className="text-neutral-700">Usage rights (90 days)</span>
              <span className="font-bold">+25% premium</span>
            </div>
          </div>
          <div className="mt-2 text-[10px] text-neutral-500 leading-snug">
            Deposit terms: 50% upfront · balance on delivery. Never accept product-only for first 3 deals — cash establishes your rate.
          </div>
        </div>

        {/* Rules */}
        <div className="rounded-lg bg-neutral-900 text-white p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-2">
            Non-negotiable rules for the 30 days
          </div>
          <ul className="text-[11.5px] leading-snug space-y-1">
            <li>1. <b>Face-free is fine.</b> Voice + real B-roll + text overlay = your signature style.</li>
            <li>2. <b>No AI-generated humans.</b> Ever. Use AI for polish clips only (transitions, hooks, concept visuals).</li>
            <li>3. <b>Post 3–5×/week minimum.</b> Missing days breaks the algorithm's trust.</li>
            <li>4. <b>Reply within 30 minutes</b> to every comment on the first hour of posting.</li>
            <li>5. <b>Reject any brand deal</b> where the product would hurt your audience's trust in you.</li>
            <li>6. <b>50% deposit before any brand video</b> — no exceptions, no matter how legit the brand looks.</li>
            <li>7. <b>Every video ends with a CTA.</b> Yellow bag, bio link, DM me, or comment prompt — always give viewers a next step.</li>
            <li>8. <b>Track weekly</b> — followers, TikTok Shop revenue, brand pipeline, FreightPilotAI signups.</li>
          </ul>
        </div>

        {/* Bottom footer */}
        <div className="mt-3 pt-2 border-t border-neutral-300 flex items-center justify-between text-[9px] text-neutral-500">
          <div>Personal plan for {name} · {handle} · Not for redistribution</div>
          <div>freightpilotai.ai</div>
        </div>
      </div>
    </div>
  );
}

function WeekBlock({ number, title, children, dateForDay }) {
  return (
    <div className="mb-4">
      <div className="flex items-baseline gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-widest bg-[#FF5A1F] text-black font-bold px-2 py-0.5 rounded">
          Week {number}
        </div>
        <div className="text-[13px] font-bold text-neutral-900">{title}</div>
      </div>
      <div className="space-y-1 pl-1">
        {Array.isArray(children)
          ? children.map((child, i) => {
              if (!child?.props) return child;
              const n = child.props.n;
              const date = dateForDay ? dateForDay(n) : null;
              return { ...child, key: i, props: { ...child.props, date } };
            })
          : children}
      </div>
    </div>
  );
}

function Day({ n, label, date }) {
  return (
    <div className="day-row flex items-start gap-2 text-[11.5px] leading-snug">
      <input type="checkbox" className="mt-0.5 shrink-0" />
      <div className="w-[52px] shrink-0 text-neutral-500 font-mono text-[10.5px] pt-0.5">
        Day {String(n).padStart(2, "0")}
        {date && <div className="text-[9px] text-neutral-400">{date}</div>}
      </div>
      <div className="text-neutral-800 flex-1" dangerouslySetInnerHTML={{ __html: label }} />
    </div>
  );
}
